import { useState, useEffect } from 'react'
import { supabase } from '../../services/supabase'
import { useAuth } from '../../hooks/useAuth'
import { usePolicies } from '../../hooks/usePolicies'
import { Table } from '../Common/Table'
import { Pagination } from '../Common/Pagination'
import { Button } from '../Common/Button'
import { PolicyForm } from './PolicyForm'
import { PolicyImport } from './PolicyImport'
import { BulkPolicyUpdateModal } from './BulkPolicyUpdateModal'

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 3500)
    return () => clearTimeout(t)
  }, [onDismiss])
  return (
    <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded shadow-lg text-white text-sm font-semibold flex items-center gap-2 ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
      {type === 'success' ? '✓' : '✗'} {message}
    </div>
  )
}

export function PoliciesList({ sidebarCollapsed, isDarkMode = false }) {
  const { canAccess, loading: authLoading } = useAuth()

  const [page, setPage] = useState(1)
  const [policySearch, setPolicySearch] = useState('')
  const [clientSearch, setClientSearch] = useState('')
  const [providerSearch, setProviderSearch] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const [sort, setSort] = useState('created_at:desc')
  const [showForm, setShowForm] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [editingPolicy, setEditingPolicy] = useState(null)
  const [showBulkUpdate, setShowBulkUpdate] = useState(false)

  // Advisor filter
  const [advisors, setAdvisors] = useState([])
  const [selectedAdvisor, setSelectedAdvisor] = useState(null)

  // Comm % filter (client-side)
  const [splitFilter, setSplitFilter] = useState('all')

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Toast
  const [toast, setToast] = useState(null)

  const [sortBy, sortOrder] = sort.split(':')
  const { policies: allPolicies, total, loading, createPolicy, updatePolicy, toggleActive, importPolicies, deletePolicies, refetch } =
    usePolicies(page, 100, policySearch, clientSearch, sortBy, sortOrder, selectedAdvisor?.id || null, providerSearch, productSearch)

  // Client-side comm% filter
  const policies = allPolicies.filter(p => {
    if (splitFilter === 'all') return true
    const splits = (p.policy_advisors || []).map(pa => pa.split_percentage).filter(s => s != null)
    if (splitFilter === 'has_split') return splits.length > 0
    if (splitFilter === 'no_split') return splits.length === 0
    if (splitFilter === 'full') return splits.some(s => s >= 100)
    if (splitFilter === 'majority') return splits.some(s => s >= 50 && s < 100)
    if (splitFilter === 'minority') return splits.some(s => s > 0 && s < 50)
    return true
  })

  // Reset splitFilter to 'all' if user loses commissions_view_all permission
  useEffect(() => {
    if (!canAccess('commissions_view_all') && splitFilter !== 'all') {
      setSplitFilter('all')
      setPage(1)
    }
  }, [canAccess])

  useEffect(() => {
    supabase.from('advisors').select('id, name, full_name').eq('active', true).order('name')
      .then(({ data }) => setAdvisors(data || []))
  }, [])

  const advisorName = (a) => a.full_name || a.name || a.email || 'Unnamed'

  const handleSave = async (formData) => {
    let savedPolicy
    if (editingPolicy) {
      await updatePolicy(editingPolicy.id, formData)
      savedPolicy = { id: editingPolicy.id }
    } else {
      savedPolicy = await createPolicy(formData)
    }
    return savedPolicy
  }

  const handleImport = async (data, onProgress) => {
    try {
      const { data: providers } = await supabase.from('product_providers').select('id, name')
      const { data: entities } = await supabase.from('entities').select('id, name')
      const { data: clients } = await supabase.from('clients').select('id, full_name')

      const providerMap = {}
      providers?.forEach(p => { if (p.name) providerMap[p.name.toLowerCase().trim()] = p.id })
      const entityMap = {}
      entities?.forEach(e => { if (e.name) entityMap[e.name.toLowerCase().trim()] = e.id })
      const clientMap = {}
      clients?.forEach(c => {
        if (!c.full_name) return
        const lower = c.full_name.toLowerCase().trim()
        clientMap[lower] = c.id
        // Also index by "Surname, First Names" format so Excel names like "Emmett, Ashley Edwin" match
        if (lower.includes(' ')) {
          const parts = lower.split(/\s+/)
          const reversed = `${parts[parts.length - 1]}, ${parts.slice(0, -1).join(' ')}`
          clientMap[reversed] = c.id
        }
      })

      const enrichedData = data.map(row => {
        const enriched = { ...row }
        delete enriched.product_provider
        delete enriched.entity
        delete enriched.client
        if (row.product_provider && typeof row.product_provider === 'string') {
          const providerId = providerMap[row.product_provider.toLowerCase().trim()]
          if (providerId) enriched.product_provider_id = providerId
        }
        if (row.entity && typeof row.entity === 'string') {
          const entityId = entityMap[row.entity.toLowerCase().trim()]
          if (entityId) enriched.entity_id = entityId
        }
        if (row.client && typeof row.client === 'string') {
          const clientLower = row.client.toLowerCase().trim()
          // Try direct match first; if comma format ("Surname, First"), also try reversed
          let clientId = clientMap[clientLower]
          if (!clientId && clientLower.includes(',')) {
            const [surname, first] = clientLower.split(',').map(p => p.trim())
            clientId = clientMap[`${first} ${surname}`]
          }
          if (clientId) enriched.client_id = clientId
        }
        return enriched
      })

      const result = await importPolicies(enrichedData, onProgress)

      // Dedup enrichedData the same way importPolicies does — merge splits from same policy
      const dedupedMap = new Map()
      enrichedData.forEach(r => {
        const key = String(r.policy_number)
        if (dedupedMap.has(key)) {
          const prev = dedupedMap.get(key)
          const merged = [...(prev._advisorSplits || []), ...(r._advisorSplits || [])]
          dedupedMap.set(key, { ...prev, _advisorSplits: merged.length ? merged : undefined })
        } else {
          dedupedMap.set(key, r)
        }
      })

      // Insert advisor splits — bulk operations (3 queries total, not 3 per policy)
      const unmatched = new Set()
      const rowsWithSplits = [...dedupedMap.values()].filter(r => r._advisorSplits?.length)
      if (rowsWithSplits.length > 0) {
        const { data: advisorRowsData } = await supabase.from('advisors').select('id, full_name')
        const advisorRows = advisorRowsData || []

        // Normalise: collapse whitespace, lowercase
        const norm = s => (s || '').toLowerCase().replace(/\s+/g, ' ').trim()

        const resolveAdvisor = (name) => {
          if (!name) return null
          const n = norm(name)
          if (!n) return null

          for (const a of advisorRows) {
            if (!a.full_name) continue
            const an = norm(a.full_name)

            // 1. Exact
            if (an === n) return a.id

            // 2. "Surname, First" ↔ "First Surname"
            if (n.includes(',')) {
              const [sn, fn] = n.split(',').map(p => p.trim())
              if (an === `${fn} ${sn}`) return a.id
            }
            if (an.includes(',')) {
              const [sn, fn] = an.split(',').map(p => p.trim())
              if (`${fn} ${sn}` === n) return a.id
            }

            // 3. One is prefix of the other (e.g. "Vian" vs "Vian Visser")
            if (an.startsWith(n + ' ') || n.startsWith(an + ' ')) return a.id

            // 4. First word matches and is >3 chars (e.g. Excel "Braam" → DB "Braam Soutsure")
            const nWords = n.split(' ')
            const anWords = an.split(' ')
            const nFirst = nWords[0], nLast = nWords[nWords.length - 1]
            const anFirst = anWords[0], anLast = anWords[anWords.length - 1]
            if (nFirst.length > 3 && nFirst === anFirst) return a.id

            // 5. Last word matches and is >3 chars (surname match)
            if (nLast.length > 3 && nLast === anLast) return a.id
          }
          return null
        }

        // 1 query: fetch all relevant policy IDs at once
        const splitPolicyNumbers = rowsWithSplits.map(r => String(r.policy_number))
        const { data: polRows } = await supabase
          .from('policies').select('id, policy_number').in('policy_number', splitPolicyNumbers)
        const numToId = {}
        ;(polRows || []).forEach(p => { numToId[p.policy_number] = p.id })

        // Build all inserts in memory
        const desiredByPolicy = new Map()
        for (const row of rowsWithSplits) {
          const polId = numToId[String(row.policy_number)]
          if (!polId) continue
          if (!desiredByPolicy.has(polId)) desiredByPolicy.set(polId, new Map())
          for (const { name, pct } of row._advisorSplits) {
            const advisor_id = resolveAdvisor(name)
            if (!advisor_id) { if (name) unmatched.add(name); continue }
            const split_percentage = pct != null && pct !== '' ? Number(pct) : null
            desiredByPolicy.get(polId).set(String(advisor_id), { policy_id: polId, advisor_id, split_percentage })
          }
        }

        const uniquePolicyIds = [...desiredByPolicy.keys()]

        if (uniquePolicyIds.length > 0) {
          const { data: existingSplits } = await supabase
            .from('policy_advisors')
            .select('policy_id, advisor_id, split_percentage')
            .in('policy_id', uniquePolicyIds)

          const existingByPolicy = new Map()
          ;(existingSplits || []).forEach(row => {
            const key = String(row.policy_id)
            if (!existingByPolicy.has(key)) existingByPolicy.set(key, new Map())
            existingByPolicy.get(key).set(String(row.advisor_id), row.split_percentage != null ? Number(row.split_percentage) : null)
          })

          const changedPolicyIds = uniquePolicyIds.filter(policyId => {
            const desired = desiredByPolicy.get(policyId) || new Map()
            const existing = existingByPolicy.get(String(policyId)) || new Map()
            if (desired.size !== existing.size) return true
            for (const [advisorId, desiredRow] of desired.entries()) {
              const existingPct = existing.get(advisorId)
              const desiredPct = desiredRow.split_percentage != null ? Number(desiredRow.split_percentage) : null
              if (existingPct !== desiredPct) return true
            }
            return false
          })

          if (changedPolicyIds.length > 0) {
            const chunkSize = 50
            for (let i = 0; i < changedPolicyIds.length; i += chunkSize) {
              const chunk = changedPolicyIds.slice(i, i + chunkSize)
              await Promise.all(
                chunk.map(id =>
                  supabase.from('policy_advisors').delete().eq('policy_id', id)
                )
              )
            }

            const allInserts = changedPolicyIds.flatMap(policyId => [...(desiredByPolicy.get(policyId)?.values() || [])])
            const { error: insErr } = await supabase
              .from('policy_advisors').insert(allInserts)
            if (insErr) throw new Error(`Split insert failed: ${insErr.message}`)
          }
        }
      }

      // Refresh list after splits are also written
      refetch()

      const { imported = 0, updated = 0 } = result || {}
      const unmatchedList = [...(unmatched || [])]
      setToast({
        message: [
          imported > 0 && `${imported} new polic${imported === 1 ? 'y' : 'ies'} added`,
          updated > 0 && `${updated} updated`,
          unmatchedList.length > 0 && `Unmatched advisors: ${unmatchedList.join(', ')}`,
        ].filter(Boolean).join(' · ') || 'Import complete',
        type: unmatchedList.length > 0 ? 'error' : 'success'
      })
      return { ...result, unmatched: unmatchedList }
    } catch (err) {
      setToast({ message: `Import failed: ${err.message}`, type: 'error' })
      throw err  // rethrow so PolicyImport shows error, not success screen
    }
  }

  const handleExport = async () => {
    try {
      // Fetch ALL policies in batches of 1000 (not just current page)
      const allRows = []
      let offset = 0
      while (true) {
        const { data, error } = await supabase
          .from('policies')
          .select('policy_number, customer_code, clients(full_name), product_providers(name), category, product, entities(name), group, notes, start_date, end_date, active, policy_advisors(split_percentage, advisors(full_name)), policy_attachments(id)')
          .order('policy_number', { ascending: true })
          .range(offset, offset + 999)
        if (error) throw error
        if (!data?.length) break
        allRows.push(...data)
        if (data.length < 1000) break
        offset += 1000
      }

      const XLSX = await import('xlsx')

      // Determine max advisors per policy so we emit enough Advisor N / % N columns
      const maxAdvisors = Math.max(1, ...allRows.map(p => (p.policy_advisors || []).length))

      const exportData = allRows.map(p => {
        const row = {
          'Policy Number': p.policy_number || '',
          'Customer Code': p.customer_code || '',
          'Client': p.clients?.full_name || '',
          'Supplier': p.product_providers?.name || '',
          'Category': p.category || '',
          'Product': p.product || '',
          'Entity': p.entities?.name || '',
          'Group': p.group || '',
          'Notes': p.notes || '',
          'Start Date': p.start_date || '',
          'End Date': p.end_date || '',
          'Active': p.active ? 'Yes' : 'No',
        }
        // Spread advisors into importable Advisor N / % N columns
        const pas = p.policy_advisors || []
        for (let i = 0; i < maxAdvisors; i++) {
          const pa = pas[i]
          row[`Advisor ${i + 1}`] = pa?.advisors?.full_name || ''
          row[`% ${i + 1}`] = pa?.split_percentage != null ? pa.split_percentage : ''
        }
        row['Docs'] = p.policy_attachments?.length ?? 0
        return row
      })

      const ws = XLSX.utils.json_to_sheet(exportData)
      const baseCols = [
        { wch: 20 }, { wch: 16 }, { wch: 28 }, { wch: 24 }, { wch: 16 },
        { wch: 24 }, { wch: 24 }, { wch: 16 }, { wch: 30 }, { wch: 12 },
        { wch: 12 }, { wch: 8 },
      ]
      const advisorCols = Array.from({ length: maxAdvisors }, () => [{ wch: 26 }, { wch: 8 }]).flat()
      ws['!cols'] = [...baseCols, ...advisorCols, { wch: 6 }]

      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Policies')
      XLSX.writeFile(wb, `policies_export_${new Date().toISOString().split('T')[0]}.xlsx`)
    } catch (err) {
      alert(`Export failed: ${err.message}`)
    }
  }

  const handleAdvisorChange = (e) => {
    const id = e.target.value
    if (!id) {
      setSelectedAdvisor(null)
    } else {
      setSelectedAdvisor(advisors.find(a => a.id === id) || null)
    }
    setPage(1)
    setSelectedIds(new Set())
  }

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === policies.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(policies.map(p => p.id)))
    }
  }

  const handleBulkDelete = async () => {
    setDeleting(true)
    try {
      await deletePolicies([...selectedIds])
      setSelectedIds(new Set())
      setShowDeleteModal(false)
      setToast({ message: `Deleted ${selectedIds.size} policies and linked transactions`, type: 'success' })
    } catch (err) {
      setToast({ message: `Delete failed: ${err.message}`, type: 'error' })
    } finally {
      setDeleting(false)
    }
  }

  const handleSort = (columnKey) => {
    const newOrder = sortBy === columnKey && sortOrder === 'asc' ? 'desc' : 'asc'
    setSort(`${columnKey}:${newOrder}`)
    setPage(1)
  }

  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          name="select-all"
          checked={policies.length > 0 && selectedIds.size === policies.length}
          onChange={toggleSelectAll}
          className="rounded"
        />
      ),
      render: (row) => (
        <input
          type="checkbox"
          name="select-row"
          checked={selectedIds.has(row.id)}
          onChange={() => toggleSelect(row.id)}
          onClick={(e) => e.stopPropagation()}
          className="rounded"
        />
      )
    },
    { key: 'policy_number', label: 'Policy No.', sortable: true },
    {
      key: 'customer_code',
      label: 'Cust. Code',
      sortable: true,
      render: (row) => row.customer_code
        ? <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">{row.customer_code}</span>
        : <span className="text-gray-400 text-xs">—</span>
    },
    {
      key: 'client',
      label: 'Client',
      sortable: true,
      render: (row) => row.clients?.full_name
        ? <span className="font-medium">{row.clients.full_name}</span>
        : <span className="text-gray-400 text-xs">—</span>
    },
    { key: 'product_provider', label: 'Supplier', sortable: true, render: (row) => row.product_providers?.name || row.product_provider || '-' },
    { key: 'category', label: 'Category', sortable: true, render: (row) => row.category || '-' },
    { key: 'product', label: 'Product', sortable: true, render: (row) => row.product || '-' },
    {
      key: 'start_date',
      label: 'Start Date',
      sortable: true,
      render: (row) => row.start_date
        ? <span className="text-xs">{row.start_date}</span>
        : <span className="text-gray-400 text-xs">—</span>
    },
    {
      key: 'end_date',
      label: 'End Date',
      sortable: true,
      render: (row) => row.end_date
        ? <span className="text-xs">{row.end_date}</span>
        : <span className="text-gray-400 text-xs">—</span>
    },
    {
      key: 'advisors',
      label: 'Advisors',
      render: (row) => {
        const pas = row.policy_advisors || []
        if (pas.length === 0) return <span className="text-gray-400 text-xs">—</span>

        // If no commission view all permission, show count only
        if (!canAccess('commissions_view_all')) {
          const count = row.advisor_count || pas.length
          if (count === 0) return <span className="text-gray-400 text-xs">—</span>
          return (
            <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {count} {count === 1 ? 'Advisor' : 'Advisors'}
            </span>
          )
        }

        // Has permission, show full chips
        return (
          <div className="flex flex-wrap gap-1">
            {pas.map(pa => (
              <span key={pa.id} className="inline-flex items-center gap-0.5 bg-orange-50 text-sfp-orange border border-orange-200 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap">
                {pa.advisors?.full_name?.split(' ')[0] ?? '?'}
                {pa.split_percentage != null && <span className="text-orange-400 ml-0.5">{pa.split_percentage}%</span>}
              </span>
            ))}
          </div>
        )
      }
    },
    {
      key: 'attachments',
      label: 'Docs',
      render: (row) => {
        const attachments = row.policy_attachments || []
        const count = attachments.length
        if (count === 0) return <span className="text-gray-400 text-xs">—</span>
        const fileNames = attachments.map(a => a.file_name).join('\n')
        return (
          <span
            className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-xs font-semibold cursor-help hover:bg-blue-200 transition"
            title={fileNames}
          >
            📄 {count}
          </span>
        )
      }
    },
    {
      key: 'active',
      label: 'Active',
      sortable: true,
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); toggleActive(row.id, row.active) }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${row.active ? 'bg-green-500' : 'bg-gray-300'}`}
          title={row.active ? 'Click to deactivate' : 'Click to activate'}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${row.active ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      )
    },
    {
      key: 'actions',
      label: '',
      sticky: 'right',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setEditingPolicy(row); setShowForm(true) }}
          className="text-sfp-orange hover:text-orange-700 text-xs font-semibold px-2 py-1 rounded hover:bg-orange-50 transition"
        >
          Edit
        </button>
      )
    },
  ]

  return (
    <div className={`p-4 h-full flex flex-col gap-4 transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-900' : 'bg-white'
    }`}>
      {/* Show loading spinner while auth is being checked to prevent data flash */}
      {authLoading && (
        <div className={`p-6 h-full flex flex-col items-center justify-center transition-colors duration-300 ${
          isDarkMode ? 'bg-slate-900' : 'bg-white'
        }`}>
          <div className="animate-spin h-6 w-6 border-4 border-sfp-orange border-t-transparent rounded-full" />
        </div>
      )}

      {/* Show access denied if user doesn't have permission */}
      {!authLoading && !canAccess('can_view_all_policies') && (
        <div className="text-center">
          <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'}`}>Access Denied</h3>
          <p className={isDarkMode ? 'text-slate-400' : 'text-gray-600'}>You don't have permission to view policies.</p>
        </div>
      )}

      {/* Main content - only show if auth is done and user has permission */}
      {!authLoading && canAccess('can_view_all_policies') && (
        <>
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}

      {/* Action Bar */}
      <div className="flex justify-between items-center gap-4 flex-shrink-0 flex-wrap">
        <div className="flex gap-2 flex-wrap items-center">
          <Button variant="primary" onClick={() => { setEditingPolicy(null); setShowForm(true) }}>
            + Add Policy
          </Button>
          <Button variant="secondary" onClick={() => setShowImport(true)}>
            ↑ Import Excel
          </Button>
          <Button variant="secondary" onClick={handleExport}>
            ↓ Export Excel
          </Button>
          {selectedIds.size > 0 && (
            <>
              <button
                onClick={() => setShowBulkUpdate(true)}
                className="px-3 py-1.5 bg-sfp-orange text-white text-sm font-semibold rounded hover:bg-orange-700 transition"
              >
                ✎ Update Selected ({selectedIds.size})
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-3 py-1.5 bg-red-600 text-white text-sm font-semibold rounded hover:bg-red-700 transition"
              >
                🗑 Delete Selected ({selectedIds.size})
              </button>
            </>
          )}

          {/* Advisor filter */}
          <select
            name="filter-advisor"
            value={selectedAdvisor?.id || ''}
            onChange={handleAdvisorChange}
            className="input-field text-sm w-44"
          >
            <option value="">All Advisors</option>
            {advisors.map(a => (
              <option key={a.id} value={a.id}>{advisorName(a)}</option>
            ))}
          </select>

          {/* Comm % filter — only visible with commissions_view_all */}
          {canAccess('commissions_view_all') && (
            <select
              name="filter-split"
              value={splitFilter}
              onChange={(e) => { setSplitFilter(e.target.value); setPage(1) }}
              className="input-field text-sm w-36"
            >
              <option value="all">All Comm %</option>
              <option value="has_split">Has Split %</option>
              <option value="no_split">No Split %</option>
              <option value="full">100% (Sole)</option>
              <option value="majority">≥50%</option>
              <option value="minority">&lt;50%</option>
            </select>
          )}
        </div>

        <div className="flex gap-2 items-center">
          <select
            name="sort"
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1) }}
            className="input-field text-sm"
          >
            <option value="created_at:desc">Date Created (Newest)</option>
            <option value="created_at:asc">Date Created (Oldest)</option>
            <option value="policy_number:asc">Policy Number A → Z</option>
            <option value="policy_number:desc">Policy Number Z → A</option>
            <option value="customer_code:asc">Customer Code A → Z</option>
            <option value="client:asc">Client A → Z</option>
            <option value="client:desc">Client Z → A</option>
            <option value="product_provider:asc">Supplier A → Z</option>
            <option value="product_provider:desc">Supplier Z → A</option>
            <option value="category:asc">Category A → Z</option>
            <option value="product:asc">Product A → Z</option>
            <option value="start_date:asc">Start Date (Oldest)</option>
            <option value="start_date:desc">Start Date (Newest)</option>
            <option value="end_date:asc">End Date (Oldest)</option>
            <option value="end_date:desc">End Date (Newest)</option>
            <option value="active:desc">Active First</option>
            <option value="active:asc">Inactive First</option>
          </select>
          <input
            id="policy-search"
            name="policy_search"
            type="text"
            placeholder="Policy number..."
            value={policySearch}
            onChange={(e) => { setPolicySearch(e.target.value); setPage(1) }}
            className="input-field w-40"
          />
          <input
            id="client-search"
            name="client_search"
            type="text"
            placeholder="Client name..."
            value={clientSearch}
            onChange={(e) => { setClientSearch(e.target.value); setPage(1) }}
            className="input-field w-40"
          />
          <input
            id="provider-search"
            name="provider_search"
            type="text"
            placeholder="Provider..."
            value={providerSearch}
            onChange={(e) => { setProviderSearch(e.target.value); setPage(1) }}
            className="input-field w-40"
          />
          <input
            id="product-search"
            name="product_search"
            type="text"
            placeholder="Product..."
            value={productSearch}
            onChange={(e) => { setProductSearch(e.target.value); setPage(1) }}
            className="input-field w-40"
          />
        </div>
      </div>

      {/* Active filter labels */}
      {(policySearch || clientSearch || selectedAdvisor || splitFilter !== 'all') && (
        <div className={`flex items-center gap-2 px-3 py-1.5 border rounded text-sm font-medium flex-shrink-0 flex-wrap transition-colors duration-300 ${
          isDarkMode
            ? 'bg-slate-800 border-slate-700 text-amber-400'
            : 'bg-orange-50 border-sfp-orange text-sfp-orange'
        }`}>
          {policySearch && <span>Policy #: <strong>{policySearch}</strong></span>}
          {clientSearch && <span>Client: <strong>{clientSearch}</strong></span>}
          {selectedAdvisor && <span>Advisor: <strong>{advisorName(selectedAdvisor)}</strong></span>}
          {splitFilter !== 'all' && <span>Split: <strong>{{has_split:'Has %', no_split:'No %', full:'100%', majority:'≥50%', minority:'<50%'}[splitFilter]}</strong></span>}
          <span className={`text-xs ml-1 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>({policies.length} {policies.length === 1 ? 'policy' : 'policies'})</span>
          <button
            onClick={() => { setPolicySearch(''); setClientSearch(''); setSelectedAdvisor(null); setSplitFilter('all'); setPage(1) }}
            className="ml-auto text-xs underline hover:no-underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Table */}
      <div className={`overflow-hidden flex flex-col flex-1 rounded-lg border transition-colors duration-300 ${
        isDarkMode
          ? 'bg-slate-800 border-slate-700'
          : 'bg-white border-gray-200 shadow-sm'
      }`}>
        <div className="overflow-auto flex-1">
          <Table
            columns={columns}
            data={policies}
            loading={loading}
            onRowClick={(row) => { setEditingPolicy(row); setShowForm(true) }}
            isDarkMode={isDarkMode}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </div>
      </div>

      {/* Pagination & Info */}
      <div className="flex-shrink-0 space-y-2">
        <div className={`flex items-center justify-between px-4 py-2 rounded border transition-colors duration-300 ${
          isDarkMode
            ? 'bg-slate-800 border-slate-700'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-sfp-darkgray'}`}>
            Total Policies: <span className="text-sfp-orange font-bold">{total}</span>
          </span>
          <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
            Showing {Math.min((page - 1) * 100 + 1, total)}-{Math.min(page * 100, total)} of {total}
          </span>
        </div>
        <Pagination currentPage={page} totalItems={total} pageSize={100} onPageChange={setPage} isDarkMode={isDarkMode} />
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl p-6 max-w-md w-full mx-4 transition-colors duration-300 ${
            isDarkMode
              ? 'bg-slate-800'
              : 'bg-white'
          }`}>
            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-slate-100' : 'text-sfp-darkgray'}`}>Delete Policies</h3>
            <p className={`mb-4 ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
              Delete <strong>{selectedIds.size}</strong> {selectedIds.size === 1 ? 'policy' : 'policies'}?
              This will also remove all linked transactions.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="btn-secondary" disabled={deleting}>
                Cancel
              </button>
              <button onClick={handleBulkDelete} disabled={deleting} className="btn-danger">
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <PolicyForm
          initialData={editingPolicy}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingPolicy(null); refetch() }}
          sidebarCollapsed={sidebarCollapsed}
          isDarkMode={isDarkMode}
        />
      )}

      {showImport && (
        <PolicyImport
          onImport={handleImport}
          onClose={() => setShowImport(false)}
          sidebarCollapsed={sidebarCollapsed}
          isDarkMode={isDarkMode}
        />
      )}

      {showBulkUpdate && (
        <BulkPolicyUpdateModal
          selectedIds={selectedIds}
          onClose={() => setShowBulkUpdate(false)}
          isDarkMode={isDarkMode}
          onSuccess={() => {
            setShowBulkUpdate(false)
            setSelectedIds(new Set())
            refetch()
            setToast({ message: `Updated ${selectedIds.size} policies`, type: 'success' })
          }}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}
        </>
      )}
    </div>
  )
}
